import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { usePageContext } from "../../context/pageContext";
import { usePermissionsContext } from "../../context/permissionContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useIdentity from "../../hooks/useIdentity";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import useItemApi from "../../http/item/api";
import useProcessingRequestApi from "../../http/processingRequest/api";
import {
  formatStatus,
  ProcessingRequest,
  ProcessingRequestStatus,
  ProcessingRequestStatuses,
} from "../../http/processingRequest/types";
import { FilterParam, PaginationQueryWithFields } from "../../types";
import { formatDateShort, formatTimestampShort } from "../../types/utils";
import { downloadFileFromData } from "../../utils";
import SvgAdd from "../icons/Add";
import SvgDelete from "../icons/Delete";
import ItemForm from "../items/ItemForm";
import Button from "../ui/Button";
import DisplayColumn from "../ui/DisplayColumn";
import { Action } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import Sidebar from "../ui/Sidebar";
import { Status } from "../ui/Status";
import Table from "../ui/Table";
import { FilterOptionsQuery } from "../ui/HeaderMenu";
import TH from "../ui/TH";
import styles from "./ProcessingRequests.module.css";

interface ProcessingRequestsProps {
  isInnerTable?: boolean;
  searchTerm?: string;
  prefix?: string;
  customFilters?: FilterParam[];
  views: string[];
}

const ProcessingRequests: FC<ProcessingRequestsProps> = ({
  isInnerTable = false,
  searchTerm,
  prefix,
  customFilters,
  views,
}) => {
  const filters = useListFilters({ isSearchable: true, prefix, defaultDesc: true });
  const debouncedValue = useDebounce(filters.state.q);
  const processingRequestApi = useProcessingRequestApi();
  const i18n = useI18n();
  const navigate = useNavigate();
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const permissions = usePermissionsContext();
  const identity = useIdentity();

  const [itemToDelete, setItemToDelete] = useState<Partial<ProcessingRequest>>();
  const [isModalSubmitting, setIsModalSubmitting] = useState<boolean>(false);
  const [selectedItemId, setSelectedItemId] = useState<string>("");

  const pageContext = usePageContext();

  const filterStatuses = customFilters?.find((f) => f.fieldname === "processingRequest.status")?.values;

  useEffect(() => {
    if (customFilters && !isInnerTable) {
      customFilters.forEach((cf) => {
        filters.setFilters(cf.fieldname, cf.values ?? []);
      });
    }
  }, [customFilters]);

  const query: PaginationQueryWithFields<ProcessingRequest> = {
    ...filters.state,
    q: searchTerm ?? debouncedValue,
    filters: [
      ...(filters.state.filters ?? []),
      ...(customFilters ?? []).filter((cf) => !(filters.state.filters ?? []).some((f) => f.fieldname === cf.fieldname)),
    ],
    fields: [
      "id",
      "createdAt",
      "finalItems",
      "author.firstName",
      "author.lastName",
      "author.id",
      "priority.name",
      "status",
      "supervisor",
      "processingType.name",
      "pickingNumber",
      "availabilityDate",
    ],
  };

  const { data, error } = processingRequestApi.useProcessingRequest(query, views);

  useEffect(() => {
    error && dispatch(showErrorNotification(i18n, error));
  }, [error]);

  useEffect(() => {
    if (!isInnerTable && !searchTerm) {
      location.pathname === "/requests/closed_and_discarded"
        ? pageContext.setPage(
            { url: "requests", title: i18n.t.common.requests },
            undefined,
            i18n.t.common.closedAndDiscarded,
          )
        : location.pathname === "/requests/on_going"
          ? pageContext.setPage({ url: "requests", title: i18n.t.common.requests }, undefined, i18n.t.common.onGoing)
          : location.pathname === "/requests/helmets"
            ? pageContext.setPage({ url: "requests", title: i18n.t.common.requests }, undefined, i18n.t.common.helmets)
            : pageContext.setPage({ title: i18n.t.common.requests });
    }
  }, [i18n.language, location.pathname]);

  const actions: Action<Partial<ProcessingRequest>>[] = [
    {
      title: i18n.t.common.delete,
      onClick: setItemToDelete,
      icon: SvgDelete,
      color: "red",
      isDisabled: (request) =>
        (request.author?.id !== identity.id || request.status !== ProcessingRequestStatuses.Open) &&
        !permissions.requestsDelete,
    },
  ];

  const getFiltersQuery: PaginationQueryWithFields<ProcessingRequest> = {
    ...query,
    filters: customFilters,
  };

  const filterOptions = {
    get: (fieldName: string): FilterOptionsQuery => {
      if (fieldName !== "processingRequest.status" || !filterStatuses?.length) {
        return processingRequestApi.getFieldFiltersQuery(fieldName, views, getFiltersQuery);
      }
      const options: FilterOptionsQuery = {
        queryKey: ["processing-request-status-filter-options", ...filterStatuses],
        queryFn: () => Promise.resolve(filterStatuses),
        initialData: filterStatuses,
      };
      return options;
    },
    update: filters.setFilters,
    getSelectedOptions: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const viewItems: { label: string; url: string }[] = [];

  if (location.pathname !== "/requests") {
    viewItems.push({ label: i18n.t.common.allRequests, url: "/requests" });
  }

  if (location.pathname !== "/requests/on_going") {
    viewItems.push({ label: i18n.t.common.onGoing, url: "/requests/on_going" });
  }

  if (location.pathname !== "/requests/helmets") {
    viewItems.push({ label: i18n.t.common.helmets, url: "/requests/helmets" });
  }

  if (location.pathname !== "/requests/closed_and_discarded") {
    viewItems.push({
      label: i18n.t.common.closedAndDiscarded,
      url: "/requests/closed_and_discarded",
    });
  }

  viewItems.push({
    label: i18n.t.common.groupedByItems,
    url: "/requests/grouped_by_items",
  });

  const tableViews = {
    title:
      location.pathname === "/requests/on_going"
        ? i18n.t.common.onGoing
        : location.pathname === "/requests/closed_and_discarded"
          ? i18n.t.common.closedAndDiscarded
          : location.pathname === "/requests/helmets"
            ? i18n.t.common.helmets
            : i18n.t.common.allRequests,
    items: viewItems,
  };

  return (
    <>
      <Table<Partial<ProcessingRequest>>
        {...(!isInnerTable && {
          buttons: {
            main: <Button buttonProps={{ onClick: () => navigate("/requests/new") }} glyph={SvgAdd} />,
          },
        })}
        views={tableViews}
        secondaryButton={
          !isInnerTable && (
            <Button
              buttonProps={{
                onClick: async () => {
                  const r = await processingRequestApi.exportExcel(query, aborter.current.signal);
                  r && downloadFileFromData(r?.data, "requests", r?.contentType);
                },
              }}
            >
              {i18n.t.common.exportExcel}
            </Button>
          )
        }
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        filters={isInnerTable ? undefined : filters}
        head={
          <tr>
            <TH fieldName="processingRequest.id" title={i18n.t.common.id} order={orderOptions} />
            <TH fieldName="processingRequest.createdAt" title={i18n.t.common.createdAt} order={orderOptions} />
            {/* <th align="left">{i18n.t.common.finalItemCode}</th> */}
            <TH
              fieldName="processingRequest.finalItems.code"
              title={`${i18n.t.common.finalItemCode} (${i18n.t.common.quantity})`}
              filters={isInnerTable ? undefined : filterOptions}
              order={undefined}
            />
            <TH
              fieldName="processingRequest.author.fullName"
              title={i18n.t.common.author}
              filters={isInnerTable ? undefined : filterOptions}
              order={orderOptions}
            />
            <TH
              fieldName="processingRequest.priority.name"
              title={i18n.t.common.priority}
              filters={isInnerTable ? undefined : filterOptions}
              order={orderOptions}
            />
            <TH
              fieldName="processingRequest.status"
              title={i18n.t.common.status}
              filters={isInnerTable ? undefined : filterOptions}
              order={orderOptions}
              formatter={(value) => formatStatus(value as ProcessingRequestStatus, i18n)}
            />
            <TH
              fieldName="processingRequest.supervisor.fullName"
              title={i18n.t.common.supervisor}
              filters={isInnerTable ? undefined : filterOptions}
              order={orderOptions}
            />
            <TH
              fieldName="processingRequest.type.name"
              title={i18n.t.common.type}
              filters={isInnerTable ? undefined : filterOptions}
              order={orderOptions}
            />
            <TH
              fieldName="processingRequest.pickingNumber"
              title={i18n.t.common.productionPickingNumber}
              filters={isInnerTable ? undefined : filterOptions}
              order={orderOptions}
            />
            <TH
              fieldName="processingRequest.availabilityDate"
              title={i18n.t.common.availabilityDate}
              order={orderOptions}
            />
            <th align="left"></th>
          </tr>
        }
        renderRow={(item) => (
          <>
            <td>
              <Link className={styles.itemLink} to={`/requests/${item.id}`}>
                {item.id}
              </Link>
            </td>
            <td>{item.createdAt ? formatTimestampShort(item.createdAt, i18n.language) : undefined}</td>
            <td>
              <DisplayColumn>
                {item.finalItems?.map((finalItem) => (
                  <div key={finalItem.code}>
                    <Link
                      className={styles.itemLink}
                      to={`/items?id=${finalItem.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedItemId(finalItem.id);
                      }}
                    >
                      {finalItem.code}
                    </Link>
                    {` (${finalItem.quantity} ${finalItem.unitOfMeasure})`}
                  </div>
                ))}
              </DisplayColumn>
            </td>
            <td>{item.author ? item.author.firstName + " " + item.author.lastName : undefined}</td>
            <td>{item.priority?.name}</td>
            <td>{item.status && <Status status={item.status} />}</td>
            <td>
              {item.supervisor ? [item.supervisor.firstName, item.supervisor.lastName].filter(Boolean).join(" ") : ""}
            </td>
            <td>{item.processingType?.name}</td>
            <td>{item.pickingNumber}</td>
            <td>{item.availabilityDate ? formatDateShort(item.availabilityDate, i18n.language) : ""}</td>
          </>
        )}
        rowActions={actions}
        onRowClick={(item) => {
          navigate(`/requests/${item.id}`);
        }}
        items={data?.items}
        total={!isInnerTable ? data?.total : undefined}
        error={error}
      />
      {!!itemToDelete && (
        <ModalDialog
          isSubmitting={isModalSubmitting}
          onClose={() => setItemToDelete(undefined)}
          title={i18n.t.processingRequests.delete}
          actions={[
            {
              title: i18n.t.common.cancel,
              // type: "secondary",
              onClick: () => setItemToDelete(undefined),
            },
            {
              title: i18n.t.common.yes,
              onClick: async () => {
                if (!itemToDelete.id) return;
                try {
                  setIsModalSubmitting(true);
                  await processingRequestApi.deleteProcessingRequest(itemToDelete.id);
                  setIsModalSubmitting(false);
                  setItemToDelete(undefined);
                } catch (e) {
                  setIsModalSubmitting(false);
                  dispatch(showErrorNotification(i18n, e));
                }
              },
            },
          ]}
        >
          {i18n.t.processingRequests.questions.delete}
        </ModalDialog>
      )}
      <ItemFormWrapper itemId={selectedItemId} onClose={() => setSelectedItemId("")} />
    </>
  );
};

export default ProcessingRequests;

type ItemFormWrapperProps = {
  itemId: string;
  onClose: () => void;
};

const ItemFormWrapper: FC<ItemFormWrapperProps> = ({ itemId, onClose }) => {
  const itemApi = useItemApi();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const itemResponse = itemApi.useItem(itemId);
  const [item, isInitialized] = useDataInitializer(itemResponse.data, itemResponse.error);

  useEffect(() => {
    itemId && itemResponse.refetch();
  }, [itemId]);

  useEffect(() => {
    itemResponse.error && dispatch(showErrorNotification(i18n, itemResponse.error));
  }, [itemResponse.error]);

  return (
    <Sidebar isOpen={item?.id === itemId} onClose={onClose} title={item?.code ?? ""}>
      <ItemForm item={item ?? undefined} isDisabled isLoading={!isInitialized} />
    </Sidebar>
  );
};
